import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "react-query";
import { PublicEcrPost } from "../../types/ecr-post";
import { BASE_API_URL } from "../../utils/constants";
import { Helmet } from "react-helmet";

// Icons
import { Linkedin, Copy, Share2, X as CloseIcon } from "lucide-react";
import { FaXTwitter, FaMastodon } from "react-icons/fa6";



const fetchBySlug = async (
  slug: string
): Promise<{ post: PublicEcrPost; shareLinks?: any; absoluteUrl?: string }> => {
  const res = await fetch(
    `${BASE_API_URL}libsense/ecr-posts/${encodeURIComponent(slug)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const BlogDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError } = useQuery(
    ["ecrPost", slug],
    () => fetchBySlug(slug!),
    { enabled: !!slug }
  );

  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Close on ESC
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Lock scroll when open
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading) return <div className="pt-24 px-4">Loading…</div>;
  if (isError || !data?.post)
    return <div className="pt-24 px-4 text-red-500">Not found.</div>;

  const p = data.post;
  const shareLinks = (data as any).shareLinks || {};
  const absoluteUrl = (data as any).absoluteUrl || window.location.href;
    const description =
    p.body?.replace(/<[^>]+>/g, "").slice(0, 150) || "Discover more on LIBSENSE ECR blog.";
  const image = p.mainImage?.url || "/assets/images/libsense.png";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      alert("Link copied to clipboard!");
      setOpen(false);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const ShareButtonsDesktop = (
    <div className="hidden sm:flex gap-2 ml-4">
      {shareLinks.x && (
        <a
          href={shareLinks.x}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded bg-gray-100 hover:bg-gray-200"
          title="Share on X"
        >
          <FaXTwitter className="w-4 h-4" />
        </a>
      )}
      {shareLinks.linkedin && (
        <a
          href={shareLinks.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          title="Share on LinkedIn"
        >
          <Linkedin className="w-4 h-4" />
        </a>
      )}
      {shareLinks.mastodon && (
        <a
          href={shareLinks.mastodon}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded bg-purple-600 text-white hover:bg-purple-700"
          title="Share on Mastodon"
        >
          <FaMastodon className="w-4 h-4" />
        </a>
      )}
      <button
        onClick={handleCopy}
        className="p-2 rounded bg-gray-100 hover:bg-gray-200"
        title="Copy link"
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto pt-24 px-4">
          <Helmet>
        <title>{p.title} | LIBSENSE ECR Blog</title>
        <meta name="description" content={description} />

        {/* Open Graph (Facebook, LinkedIn, WhatsApp, etc.) */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={p.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={absoluteUrl} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="LIBSENSE" />

        {/* Twitter/X cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={p.title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
      </Helmet>
      <Link to="/ecr-blog" className="text-xs underline">
        ← Back to blog
      </Link>

      <div className="flex items-start justify-between mt-2">
        <h1 className="text-3xl font-bold">{p.title}</h1>

        {/* Desktop share row */}
        {ShareButtonsDesktop}

        {/* Mobile share trigger */}
        <div className="sm:hidden ml-2">
          <button
            onClick={() => setOpen(true)}
            className="p-2 rounded bg-gray-100 hover:bg-gray-200"
            aria-label="Open share menu"
            title="Share"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-1">
        {new Date(p.date).toLocaleDateString()} • {p.author}
      </p>

      {p.mainImage?.url && (
        <img
          src={p.mainImage.url}
          alt={p.mainImage.alt || p.title}
          className="mt-4 w-full rounded"
        />
      )}

      {/* Body */}
      <div
        className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert mt-6"
        dangerouslySetInnerHTML={{ __html: p.body || "" }}
      />

      {/* Mobile bottom sheet */}
      {/* Backdrop */}
      <div
        aria-hidden
        className={`fixed inset-0 bg-black/40 transition-opacity sm:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Share options"
        className={`fixed inset-x-0 bottom-0 sm:hidden z-50 rounded-t-2xl bg-white shadow-2xl transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Handle / header */}
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-gray-300" />
          <button
            onClick={() => setOpen(false)}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-800"
            aria-label="Close share menu"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <h3 className="text-sm font-semibold mb-3">Share</h3>
          <div className="grid grid-cols-4 gap-3 text-center">
            {shareLinks.x && (
              <a
                href={shareLinks.x}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 rounded-lg border p-3 hover:bg-gray-50"
                onClick={() => setOpen(false)}
              >
                <FaXTwitter className="w-5 h-5" />
                <span className="text-xxs">X</span>
              </a>
            )}
            {shareLinks.linkedin && (
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 rounded-lg border p-3 hover:bg-gray-50"
                onClick={() => setOpen(false)}
              >
                <Linkedin className="w-5 h-5 text-blue-600" />
                <span className="text-xxs">LinkedIn</span>
              </a>
            )}
            {shareLinks.mastodon && (
              <a
                href={shareLinks.mastodon}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 rounded-lg border p-3 hover:bg-gray-50"
                onClick={() => setOpen(false)}
              >
                <FaMastodon className="w-5 h-5 text-purple-600" />
                <span className="text-xxs">Mastodon</span>
              </a>
            )}
            <button
              onClick={handleCopy}
              className="flex flex-col items-center gap-2 rounded-lg border p-3 hover:bg-gray-50"
            >
              <Copy className="w-5 h-5" />
              <span className="text-xxs">Copy</span>
            </button>
          </div>

          {/* Optional share URL display */}
          <div className="mt-4 rounded-md bg-gray-50 p-3 text-xs break-all">
            {absoluteUrl}
          </div>

          <button
            onClick={() => setOpen(false)}
            className="mt-4 w-full rounded-lg bg-gray-900 text-white py-2 text-sm hover:bg-black"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
